import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import { CopyableValue } from './CopyableValue';
import { truncateMiddle } from './format';
import type { GasWallet } from './types';

type Props = {
  wallets: GasWallet[];
};

/** One row per chain: the gas wallet's address and its live native balance, flagged when low. */
export function GasWalletsTable({ wallets }: Props) {
  const { t } = useTranslation('manageCryptoPayments');

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('gasWallets.columns.chain')}</TableHead>
          <TableHead>{t('gasWallets.columns.address')}</TableHead>
          <TableHead className="text-right">{t('gasWallets.columns.balance')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {wallets.map((wallet) => (
          <TableRow key={wallet.chainId}>
            <TableCell className="whitespace-nowrap">{wallet.chainName}</TableCell>
            <TableCell>
              <CopyableValue
                value={wallet.address}
                display={truncateMiddle(wallet.address)}
                label={t('gasWallets.columns.address')}
                className="text-xs"
              />
            </TableCell>
            <TableCell className="whitespace-nowrap text-right">
              {wallet.balance === null ? (
                <span className="text-muted-foreground text-xs" title={wallet.error ?? undefined}>
                  {t('gasWallets.balanceUnavailable')}
                </span>
              ) : (
                <span className="inline-flex items-center gap-2">
                  <span className="font-mono text-sm">
                    {wallet.balance} {wallet.nativeSymbol}
                  </span>
                  {wallet.lowBalance && <Badge variant="yellow">{t('gasWallets.low')}</Badge>}
                </span>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
